import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Calendar, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/invoices/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungen:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const cardStyle = {
    backgroundColor: '#1A1F2E',
    borderColor: '#2D3748',
    border: '1px solid',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '16px'
  };

  const invoiceItemStyle = {
    backgroundColor: '#2D3748',
    borderColor: '#4A5568',
    border: '1px solid',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#0B0F14', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '30px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
            Rechnungen
          </h1>
          <p style={{ color: '#A0AEC0' }}>
            Übersicht aller Ihrer Zahlungen und Rechnungen
          </p>
        </div>

        <div style={cardStyle}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#A0AEC0' }}>
              Lade Rechnungen...
            </div>
          ) : invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <FileText size={48} style={{ color: '#4A5568', marginBottom: '16px' }} />
              <p style={{ color: '#A0AEC0', fontSize: '16px' }}>
                Noch keine Rechnungen vorhanden
              </p>
              <p style={{ color: '#94A3B8', fontSize: '14px', marginTop: '8px' }}>
                Ihre Rechnungen erscheinen hier nach erfolgreicher Zahlung
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#E2E8F0' }}>
                  Alle Rechnungen ({invoices.length})
                </h2>
              </div>

              {invoices.map((invoice) => (
                <div key={invoice.invoice_id} style={invoiceItemStyle}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <FileText size={20} style={{ color: '#7CD4FD' }} />
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#E2E8F0' }}>
                        {invoice.invoice_id}
                      </h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '4px' }}>
                          Produkt
                        </div>
                        <div style={{ fontSize: '14px', color: '#E2E8F0', fontWeight: '500' }}>
                          {invoice.plan_name}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '4px' }}>
                          Betrag
                        </div>
                        <div style={{ fontSize: '14px', color: '#E2E8F0', fontWeight: '500' }}>
                          {formatAmount(invoice.amount)}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '4px' }}>
                          <Calendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          Zahlungsdatum
                        </div>
                        <div style={{ fontSize: '14px', color: '#E2E8F0' }}>
                          {formatDate(invoice.payment_date)}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', marginBottom: '4px' }}>
                          <CreditCard size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          Transaktions-ID
                        </div>
                        <div style={{ fontSize: '12px', color: '#94A3B8', fontFamily: 'monospace' }}>
                          {invoice.stripe_transaction_id}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ paddingLeft: '24px' }}>
                    <button
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #7CD4FD',
                        backgroundColor: 'transparent',
                        color: '#7CD4FD',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.get(`${API_URL}/api/invoices/${invoice.invoice_id}/pdf`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `Rechnung_${invoice.invoice_id}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (error) {
                          console.error('Fehler beim Herunterladen:', error);
                        }
                      }}
                    >
                      <Download size={16} />
                      PDF
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div style={{
          marginTop: '24px',
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: '#1e3a5f',
          border: '1px solid #1EA0E6'
        }}>
          <p style={{ color: '#E2E8F0', fontSize: '14px', margin: 0 }}>
            <strong>💡 Hinweis:</strong> Alle Rechnungen werden automatisch nach erfolgreicher Zahlung erstellt. 
            Sie können jederzeit eine PDF-Version herunterladen.
          </p>
        </div>
      </div>
    </div>
  );
};
